import { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRight } from "lucide-react";
import { analytics } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "")}/api`;

const ROLE_META = {
  buyer:      { label: "Importer / Buyer",   verb: "buy and execute structured trade flows",        color: "text-blue-400",   volumeLabel: "Annual import / purchase volume (USD)",   placeholder: "e.g., Vannamei shrimp · India ↔ USA · 50 MT/month" },
  supplier:   { label: "Exporter",           verb: "list verified inventory and unlock faster receivables", color: "text-purple-400", volumeLabel: "Annual export volume (USD)",       placeholder: "e.g., Premium basmati · India → UAE · 200 MT/month" },
  exporter:   { label: "Exporter",           verb: "list verified inventory and unlock faster receivables", color: "text-purple-400", volumeLabel: "Annual export volume (USD)",       placeholder: "e.g., Premium basmati · India → UAE · 200 MT/month" },
  downstream: { label: "Downstream Buyer",   verb: "plan inventory smarter and get reliable supply",        color: "text-cyan-400",   volumeLabel: "Annual sourcing volume (USD)",     placeholder: "e.g., Retail chain · 20 SKUs · sourcing $5M/year" },
  investor:   { label: "Investor",           verb: "deploy capital across milestone-secured trade flows",   color: "text-[#22C55E]",  volumeLabel: "Investment ticket size (USD)",     placeholder: "e.g., Family office · agritech focus · $1M-$3M ticket" },
};

const TRADER_VOLUMES = [
  { value: "<$1M",        label: "Less than $1M" },
  { value: "$1M-$5M",     label: "$1M – $5M" },
  { value: "$5M-$25M",    label: "$5M – $25M" },
  { value: "$25M-$100M",  label: "$25M – $100M" },
  { value: "$100M+",      label: "$100M+" },
];

const INVESTOR_TICKETS = [
  { value: "<$100K",        label: "Less than $100K" },
  { value: "$100K-$500K",   label: "$100K – $500K" },
  { value: "$500K-$2M",     label: "$500K – $2M" },
  { value: "$2M-$10M",      label: "$2M – $10M" },
  { value: "$10M+",         label: "$10M+" },
];

export function ApplicationModal({ open, onOpenChange, role }) {
  const [form, setForm] = useState({ full_name: "", email: "", company: "", message: "", annual_volume: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const meta = ROLE_META[role] || ROLE_META.buyer;
  const isInvestor = role === "investor";
  const volumeOptions = isInvestor ? INVESTOR_TICKETS : TRADER_VOLUMES;

  // Backend role mapping
  const submitRole = role === "exporter" ? "supplier"
    : role === "downstream" ? "downstream"
    : role === "investor" ? "investor"
    : "buyer";
  // Welcome page slug
  const welcomeSlug = (role === "supplier" || role === "exporter") ? "exporter"
    : role === "downstream" ? "downstream"
    : role === "investor" ? "investor"
    : "buyer";

  useEffect(() => {
    if (open) analytics.applicationStarted(welcomeSlug);
  }, [open, welcomeSlug]);

  // Reset volume when role changes (different option sets)
  useEffect(() => { setForm((p) => ({ ...p, annual_volume: "" })); }, [role]);

  const handleChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  const handleVolumeChange = (val) => setForm((p) => ({ ...p, annual_volume: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.annual_volume) {
      setError(isInvestor ? "Please select your investment ticket size." : "Please select your annual volume.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/applications`, { ...form, role: submitRole });
      analytics.applicationSubmitted(welcomeSlug, data?.id);
      const firstName = (form.full_name || "").trim().split(/\s+/)[0] || "";
      const qs = new URLSearchParams();
      if (firstName) qs.set("name", firstName);
      if (data?.id) qs.set("app_id", data.id);
      window.location.href = `/welcome/${welcomeSlug}${qs.toString() ? "?" + qs.toString() : ""}`;
    } catch (err) {
      console.error(err);
      setError("Submission failed. Please try again.");
      setLoading(false);
    }
  };

  const handleClose = (val) => {
    onOpenChange(val);
    if (!val) setTimeout(() => setForm({ full_name: "", email: "", company: "", message: "", annual_volume: "" }), 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="bg-[#09120D] border-[#1A2E22] text-white max-w-md max-h-[90vh] overflow-y-auto"
        data-testid="application-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-white font-['Outfit'] text-xl font-semibold">
            Apply as <span className={meta.color}>{meta.label}</span>
          </DialogTitle>
          <DialogDescription className="text-[#A1B2A8]">
            Verified network only. Tell us about your business and you&apos;ll {meta.verb} on Delta GreenX.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2" data-testid="application-form">
          <div className="space-y-2">
            <Label htmlFor="full_name" className="text-[#A1B2A8] text-sm">Full Name</Label>
            <Input id="full_name" name="full_name" value={form.full_name} onChange={handleChange} required
              className="bg-[#050A07] border-[#1A2E22] text-white placeholder:text-[#A1B2A8]/40 focus:border-[#22C55E] focus:ring-[#22C55E]/20"
              placeholder="Your full name" data-testid="input-full-name" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email" className="text-[#A1B2A8] text-sm">Email</Label>
            <Input id="email" name="email" type="email" value={form.email} onChange={handleChange} required
              className="bg-[#050A07] border-[#1A2E22] text-white placeholder:text-[#A1B2A8]/40 focus:border-[#22C55E] focus:ring-[#22C55E]/20"
              placeholder="you@company.com" data-testid="input-email" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="company" className="text-[#A1B2A8] text-sm">Company</Label>
            <Input id="company" name="company" value={form.company} onChange={handleChange} required
              className="bg-[#050A07] border-[#1A2E22] text-white placeholder:text-[#A1B2A8]/40 focus:border-[#22C55E] focus:ring-[#22C55E]/20"
              placeholder="Company name" data-testid="input-company" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="annual_volume" className="text-[#A1B2A8] text-sm">
              {meta.volumeLabel} <span className="text-[#22C55E]">*</span>
            </Label>
            <Select value={form.annual_volume} onValueChange={handleVolumeChange}>
              <SelectTrigger
                id="annual_volume"
                className="bg-[#050A07] border-[#1A2E22] text-white data-[placeholder]:text-[#A1B2A8]/40 focus:border-[#22C55E] focus:ring-[#22C55E]/20 h-10"
                data-testid="input-annual-volume"
              >
                <SelectValue placeholder={isInvestor ? "Select ticket size" : "Select volume range"} />
              </SelectTrigger>
              <SelectContent className="bg-[#09120D] border-[#1A2E22] text-white">
                {volumeOptions.map((v) => (
                  <SelectItem key={v.value} value={v.value} className="focus:bg-[#0D1B13] focus:text-white" data-testid={`volume-opt-${v.value}`}>
                    {v.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[#A1B2A8]/50 text-[10px]">
              {isInvestor
                ? "Helps us route you to corridors / vehicles that match your ticket size."
                : "Helps us match you with corridors and capital vehicles aligned to your scale."}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="text-[#A1B2A8] text-sm">
              {isInvestor ? "Investment focus / mandate (Optional)" : "Trade activity & geography (Optional)"}
            </Label>
            <Textarea id="message" name="message" value={form.message} onChange={handleChange}
              className="bg-[#050A07] border-[#1A2E22] text-white placeholder:text-[#A1B2A8]/40 focus:border-[#22C55E] focus:ring-[#22C55E]/20 resize-none"
              placeholder={meta.placeholder}
              rows={3} data-testid="input-message" />
          </div>

          {error && (
            <p className="text-red-400 text-xs" data-testid="application-error">{error}</p>
          )}

          <button type="submit" disabled={loading}
            className="w-full bg-[#22C55E] text-[#050A07] hover:bg-[#1CAB4D] transition-all px-8 py-3 font-medium font-['Outfit'] disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="submit-application-btn">
            {loading ? "Submitting..." : <>Submit & Preview Your Dashboard <ArrowRight className="w-4 h-4" /></>}
          </button>
          <p className="text-[#A1B2A8]/50 text-[10px] text-center">After submit you&apos;ll instantly see what you unlock post-approval.</p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
